import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { GlassCard } from './GlassCard';
import { useUserStore } from '../store/userStore';
import { getMessages, sendMessage, getNotifications, markNotificationRead, markAllMessagesRead } from '../lib/api';

export function CareTeamMessages({ userId }) {
  const refreshUnread = useUserStore((s) => s.refreshUnread);
  const threadRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    getMessages(userId)
      .then((m) => setMessages((m || []).slice().reverse()))
      .catch(() => {});
    getNotifications(userId)
      .then((n) => setReminders((n || []).filter((x) => x.notification_type === 'consultation_reminder')))
      .catch(() => {})
      .finally(() => setLoaded(true));
  };

  useEffect(() => {
    if (!userId) return;
    load();
    // Let the badge linger briefly so the patient notices the new note, then clear it
    const t = setTimeout(() => {
      markAllMessagesRead(userId)
        .then((r) => { if (r?.updated) refreshUnread(); })
        .catch(() => {});
    }, 3500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [userId]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await sendMessage({ user_id: userId, sender_type: 'user', content: text.trim() });
      setText('');
      load();
    } catch (e) {
      toast.error('Could not send message');
    } finally {
      setSending(false);
    }
  };

  const dismissReminder = async (id) => {
    setReminders((r) => r.filter((x) => x.id !== id));
    await markNotificationRead(id).catch(() => {});
    refreshUnread();
  };

  if (!loaded) return null;
  if (messages.length === 0 && reminders.length === 0) {
    return (
      <GlassCard className="p-5" data-testid="care-team-empty">
        <div className="flex items-center gap-2 mb-1">
          <i className="fa-solid fa-comment-medical text-[var(--aura-teal)]" />
          <h3 className="font-display text-lg">Your care team</h3>
        </div>
        <p className="text-sm text-[var(--aura-ink-soft)]">Messages and follow-up reminders from your clinician will appear here.</p>
        <div className="flex gap-2 mt-3">
          <input
            data-testid="patient-message-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Message your clinician…"
            className="flex-1 rounded-xl border border-[var(--aura-line)] px-3 py-2.5 outline-none focus:border-[var(--aura-purple)]"
          />
          <button data-testid="patient-send-btn" onClick={send} disabled={sending} className="aura-btn-gradient text-white rounded-xl px-4 disabled:opacity-70">
            {sending ? <i className="fa-solid fa-circle-notch fa-spin" /> : <i className="fa-solid fa-paper-plane" />}
          </button>
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="p-5" data-testid="care-team-messages">
      <div className="flex items-center gap-2 mb-3">
        <i className="fa-solid fa-comment-medical text-[var(--aura-teal)]" />
        <h3 className="font-display text-lg">Your care team</h3>
      </div>

      {reminders.map((r) => (
        <div key={r.id} className="flex items-start gap-3 rounded-xl p-3 mb-2" style={{ background: 'rgba(236,72,153,0.08)' }} data-testid={`reminder-${r.id}`}>
          <i className="fa-solid fa-bell mt-0.5" style={{ color: '#EC4899' }} />
          <div className="flex-1">
            <div className="font-semibold text-sm">{r.title}</div>
            <div className="text-sm text-[var(--aura-ink-soft)]">{r.message}</div>
          </div>
          <button data-testid={`reminder-dismiss-${r.id}`} aria-label="Dismiss reminder" onClick={() => dismissReminder(r.id)} className="text-[var(--aura-ink-soft)]"><i className="fa-solid fa-xmark" /></button>
        </div>
      ))}

      {messages.length > 0 && (
        <div ref={threadRef} className="max-h-52 overflow-y-auto space-y-2 my-3 pr-1">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className="max-w-[80%] rounded-2xl px-3 py-2 text-sm"
                style={m.sender_type === 'user' ? { background: 'var(--aura-purple)', color: '#fff' } : { background: 'rgba(124,58,237,0.08)', border: '1px solid var(--aura-line)' }}
              >
                {m.content}
                <div className="text-[10px] opacity-60 mt-1">
                  {m.sender_type === 'clinician' ? 'Clinician · ' : 'You · '}
                  {new Date(m.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mt-2">
        <input
          data-testid="patient-message-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Reply to your clinician…"
          className="flex-1 rounded-xl border border-[var(--aura-line)] px-3 py-2.5 outline-none focus:border-[var(--aura-purple)]"
        />
        <button data-testid="patient-send-btn" onClick={send} disabled={sending} className="aura-btn-gradient text-white rounded-xl px-4 disabled:opacity-70">
          {sending ? <i className="fa-solid fa-circle-notch fa-spin" /> : <i className="fa-solid fa-paper-plane" />}
        </button>
      </div>
    </GlassCard>
  );
}
